import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getCompletedGroups,
  getCompletedSelfAssessments,
  BASE_URL,
} from "../../axios/axiosFunctions";
import { downloadSelfAssessmentPDF } from "../../utils/downloadSelfAssessment";

const slugForCategory = (category) => {
  switch (category) {
    case "tim":
      return "tim_self_assessment";
    case "executive":
      return "executive_team_assessment";
    case "lim360":
      return "lim_360_assessment";
    case "mim360":
      return "mim_360_assessment";
    case "simple360":
      return "simple_360_feedback";
    default:
      return category;
  }
};

const SELF_LABEL = {
  mim_self_assessment: "MIM Self",
  lim_self_assessment: "LIM Self",
};

const formatDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "-";
  }
};

const CompletedReportsAdmin = () => {
  const [groups, setGroups] = useState([]);
  const [selfRows, setSelfRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const [groupData, selfData] = await Promise.all([
        getCompletedGroups(search),
        getCompletedSelfAssessments(search),
      ]);
      if (!cancelled) {
        setGroups(groupData);
        setSelfRows(selfData);
        setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  const summary = useMemo(() => {
    const byCategory = groups.reduce((acc, g) => {
      acc[g.category] = (acc[g.category] || 0) + 1;
      return acc;
    }, {});
    selfRows.forEach((r) => {
      const key = SELF_LABEL[r.assessmentSlug] || r.assessmentSlug;
      byCategory[key] = (byCategory[key] || 0) + 1;
    });
    return byCategory;
  }, [groups, selfRows]);

  const handleDownloadSelf = async (row) => {
    const key = `${row.userId}__${row.assessmentSlug}`;
    setDownloadingKey(key);
    setDownloadError("");
    try {
      await downloadSelfAssessmentPDF({
        userId: row.userId,
        slug: row.assessmentSlug,
        name: `${row.firstName || ""} ${row.lastName || ""}`.trim(),
      });
    } catch (err) {
      console.log("Self-assessment PDF download failed:", err);
      setDownloadError(
        "Could not generate the PDF. Please try again or check the console.",
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ marginBottom: "20px" }}
      >
        <Typography variant="h3" color="secondary" fontWeight={500}>
          Completed Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          href={`${BASE_URL}group/export`}
          target="_blank"
        >
          Export All Data (CSV)
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ marginBottom: "20px", gap: 1 }}>
        {Object.entries(summary).map(([cat, count]) => (
          <Chip key={cat} label={`${cat}: ${count}`} variant="outlined" />
        ))}
      </Stack>

      <TextField
        fullWidth
        label="Search by name, company, team, or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ marginBottom: "20px" }}
      />

      {downloadError && (
        <Alert severity="error" sx={{ marginBottom: "16px" }} onClose={() => setDownloadError("")}>
          {downloadError}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ padding: "40px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h5" color="secondary" fontWeight={500} sx={{ marginTop: "8px", marginBottom: "12px" }}>
            Self-Assessments
          </Typography>
          {selfRows.length === 0 ? (
            <Alert severity="info" sx={{ marginBottom: "32px" }}>
              No completed self-assessments found.
            </Alert>
          ) : (
            <TableContainer sx={{ marginBottom: "32px" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assessment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Report
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selfRows.map((r) => {
                    const key = `${r.userId}__${r.assessmentSlug}`;
                    const isDownloading = downloadingKey === key;
                    return (
                      <TableRow key={key} hover>
                        <TableCell>
                          {`${r.firstName || ""} ${r.lastName || ""}`.trim() || "-"}
                        </TableCell>
                        <TableCell>{r.email || "-"}</TableCell>
                        <TableCell>{r.companyName || "-"}</TableCell>
                        <TableCell>
                          {SELF_LABEL[r.assessmentSlug] || r.assessmentSlug}
                        </TableCell>
                        <TableCell>{formatDate(r.completedDate)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download PDF">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadSelf(r)}
                                disabled={isDownloading}
                              >
                                {isDownloading ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="h5" color="secondary" fontWeight={500} sx={{ marginTop: "8px", marginBottom: "12px" }}>
            Team Reports
          </Typography>
          {groups.length === 0 ? (
            <Alert severity="info">No completed team reports found.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Launched</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Deadline</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Report
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((g) => {
                    const slug = slugForCategory(g.category);
                    const reportUrl = `/${slug}/${g._id}/report`;
                    return (
                      <TableRow key={g._id} hover>
                        <TableCell>{g.company?.name || "-"}</TableCell>
                        <TableCell>{g.name}</TableCell>
                        <TableCell>{g.category}</TableCell>
                        <TableCell>{formatDate(g.launchDate)}</TableCell>
                        <TableCell>{formatDate(g.deadline)}</TableCell>
                        <TableCell align="right">
                          {g.category === "simple360" ? (
                            // Simple 360 Feedback has no report page — responses
                            // are exported to XLSX from the team dialog instead.
                            <Typography variant="caption" color="textSecondary">
                              XLSX export
                            </Typography>
                          ) : (
                            <IconButton
                              component="a"
                              href={reportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default CompletedReportsAdmin;
